/**
 * What this project's agents may do with tools: the policy that applies to the project, read as
 * a machine would receive it, with each published server's tools counted by what happens to a call.
 */

import { toolDecision, type ToolAction } from '@aioe/shared';
import { Link } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';

const UNLISTED = { allow: 'allowed', ask: 'asked about first', block: 'blocked' } as const;

export function ProjectTools({ projectId }: { projectId: string }) {
  const policy = useLoad(() => routes.effectivePolicy(projectId), [projectId]);
  const tools = policy.data?.policy.tools;
  const servers = (tools?.servers ?? []).filter((s) => s.status === 'published');
  return (
    <ListGroup
      title="Tools"
      count={tools ? servers.length || undefined : undefined}
      description="The tool servers this project's agents may use, and what happens to a call to each."
      actions={
        <Link to="/tools" className="text-xs text-accent hover:underline">
          Open Tools
        </Link>
      }
    >
      {policy.error ? (
        <ListBlock>
          <ErrorNote error={policy.error} />
        </ListBlock>
      ) : null}
      {!policy.data && !policy.error ? <ListBlock className="text-sm text-muted">Reading the policy…</ListBlock> : null}
      {policy.data && !tools ? <ListRow title="Nothing is set" description="Agents use the tool servers each repository brings, as they always have." /> : null}
      {tools ? (
        <ListRow
          title={tools.enforcement === 'enforce' ? 'Calls are checked' : 'Calls are watched'}
          description={`${tools.enforcement === 'enforce' ? 'A call that is Off is refused and one that is Ask first waits for a person.' : 'Nothing is stopped yet: what would be refused or held is recorded.'} A server that is not in the catalogue is ${UNLISTED[tools.unlistedServers]}.`}
          status={<StatusMark state={tools.enforcement === 'enforce' ? 'connected' : 'medium'} label={tools.enforcement === 'enforce' ? 'Enforced' : 'Watching only'} />}
        />
      ) : null}
      {tools
        ? servers.map((s) => {
            const counts: Record<ToolAction, number> = { allow: 0, ask: 0, off: 0 };
            // What would happen to each tool as it was reviewed, whatever the rollout lever says today.
            for (const [name, t] of Object.entries(s.tools)) counts[toolDecision({ ...tools, enforcement: 'enforce' }, { fingerprint: s.fingerprint, tool: name, ...(t.hash ? { hash: t.description ? '1'.repeat(64) : t.hash } : {}) }).action]++;
            return <ListRow key={s.fingerprint} title={s.name} description={`${counts.allow} allowed · ${counts.ask} ask first · ${counts.off} off`} to={`/tools/${s.fingerprint}`} />;
          })
        : null}
    </ListGroup>
  );
}
